import { AccountInvestFlowType, InvestMetadata } from 'types/account/invest';
import { CheckoutOrderGroupDetails, CrossSellResponse } from 'types/checkout';
import { createOrderGroup, placeOrder } from '@api/checkout';
import { getCrossSellEligibility, getOfferingReservationWindows, getOrderMetadata } from '@api/invest';
import { defineStore } from 'pinia';
import { DocumentListItem } from 'types/documents';
import { K1Acknowledgement } from 'types/form';
import { OrderGroupResponse } from 'types/invest';
import { PaymentMethod } from 'types/payment';
import { ref } from 'vue';
import { ReservationWindow } from 'types/reit-element';
import { useAppStore } from './app';
import { useInvestmentEntityStore } from './investment-entity';
import { useInvestmentTriggerStore } from './investment-trigger';

export const useInvestStore = defineStore('invest', () => {
	const accountInvestFlowType = ref<AccountInvestFlowType | null>(null);
	const acknowledgedK1Disclosure = ref<K1Acknowledgement>({ acknowledged: false });
	const amount = ref<string | null>(null);
	const amountAltText = ref<string | null>(null);
	const crossSellResponse = ref<CrossSellResponse | null>(null);
	const hasMultipleWindows = ref(false);
	const investMetaData = ref<InvestMetadata | null>(null);
	const investTabAddFunds = ref(false);
	const isInTestGroup = ref<boolean | null>(null);
	const offeringSubmissionErrorCode = ref<string | null>(null);
	const orderComplete = ref(false);
	const orderGroupId = ref<string | null>(null);
	const orderGroupResponse = ref<OrderGroupResponse | null>(null);
	const paymentMethod = ref<PaymentMethod | null>(null);
	const reservationWindows = ref<Array<ReservationWindow>>([]);
	const selectedWindow = ref<ReservationWindow | null>(null);
	const displayAllocationStep = ref<boolean>(false);

	function $reset(): void {
		accountInvestFlowType.value = null;
		amount.value = null;
		amountAltText.value = null;
		investMetaData.value = null;
		investTabAddFunds.value = false;
		offeringSubmissionErrorCode.value = null;
		orderComplete.value = false;
		orderGroupId.value = null;
		orderGroupResponse.value = null;
		displayAllocationStep.value = false;
	}

	function updateReservationWindow(data: {
		reservationWindows: Array<ReservationWindow>;
		cohortId: string | null;
	}): void {
		reservationWindows.value = data.reservationWindows;
		hasMultipleWindows.value =
			data.reservationWindows.filter((window) => window.status.name === 'AVAILABLE').length > 1;
		selectedWindow.value = data.cohortId
			? data.reservationWindows.find((window) => {
					return window.cohortId === data.cohortId;
				}) ?? null
			: null;
	}

	function resetReservationWindowData(): void {
		updateReservationWindow({ reservationWindows: [], cohortId: null });
	}

	async function determineTestGroup(): Promise<boolean> {
		if (isInTestGroup.value === null) {
			const appStore = useAppStore();
			isInTestGroup.value = await appStore.isUserInLDTestGroup({
				featureFlag: '2025-06-23-investment-flows-final-UI-unification-web',
				testGroup: 'test-investment-flows-final-UI-unification-web'
			});
		}

		return isInTestGroup.value;
	}

	async function fetchAndUpdateReservationWindow(data: {
		offeringId: string;
		cohortId: string | null;
	}): Promise<void> {
		const windows = await getOfferingReservationWindows(data.offeringId);
		updateReservationWindow({ reservationWindows: windows, cohortId: data.cohortId });
	}

	async function getInvestOrderMetadata(reitOfferingVintageId?: string, cohortId?: string): Promise<InvestMetadata> {
		const data = await getOrderMetadata(reitOfferingVintageId, cohortId);
		investMetaData.value = data;
		return data;
	}

	async function checkCrossSellEligibility(): Promise<void> {
		const response = await getCrossSellEligibility();
		crossSellResponse.value = response;
	}

	async function createInvestOrderGroup(
		orderDetails: CheckoutOrderGroupDetails,
		isPlanFlowType = false
	): Promise<string> {
		const order: CheckoutOrderGroupDetails = {
			amount: orderDetails.amount,
			paymentMethodId: orderDetails.paymentMethodId,
			paymentMethodType: orderDetails.paymentMethodType
		};
		const investmentTriggerStore = useInvestmentTriggerStore();

		if (orderDetails.investmentTrigger) {
			order.investmentTrigger = orderDetails.investmentTrigger;
			investmentTriggerStore.investmentTrigger = orderDetails.investmentTrigger;
		}

		if (isPlanFlowType) {
			order.riaPlanId = orderDetails.riaPlanId;
		} else {
			order.reitOfferingVintageId = orderDetails.reitOfferingVintageId;
		}

		if (orderDetails.cohortId) {
			order.cohortId = orderDetails.cohortId;
		}

		const orderGroup = await createOrderGroup(order);

		if (orderDetails.amount) {
			amount.value = orderDetails.amount;
		}

		orderGroupId.value = orderGroup.orderGroupId ?? null;

		const isInTest = await determineTestGroup();

		if (isInTest && displayAllocationStep.value) {
			return 'account-invest-flow-allocation';
		}

		return 'account-invest-flow-review';
	}

	async function submitReview(data: {
		reitDocuments: Array<DocumentListItem>;
		suppressToast?: boolean;
	}): Promise<string> {
		const investmentEntityStore = useInvestmentEntityStore();
		const reitDocumentRevisionIds = data.reitDocuments.map((document) => document.documentRevisionId ?? '');
		const response = await placeOrder(
			orderGroupId.value ?? '',
			reitDocumentRevisionIds,
			data.suppressToast ?? false
		);

		orderGroupResponse.value = response;
		orderComplete.value = true;

		await investmentEntityStore.refreshPositionsAndPerformance();

		return 'account-invest-flow-success';
	}

	return {
		accountInvestFlowType,
		acknowledgedK1Disclosure,
		amount,
		amountAltText,
		crossSellResponse,
		hasMultipleWindows,
		investMetaData,
		investTabAddFunds,
		offeringSubmissionErrorCode,
		orderComplete,
		orderGroupId,
		orderGroupResponse,
		paymentMethod,
		reservationWindows,
		selectedWindow,
		displayAllocationStep,
		$reset,
		checkCrossSellEligibility,
		createInvestOrderGroup,
		determineTestGroup,
		fetchAndUpdateReservationWindow,
		getInvestOrderMetadata,
		resetReservationWindowData,
		submitReview,
		updateReservationWindow
	};
});
